import { createTheme, alpha } from '@mui/material/styles';

const PURPLE = '#7c6ff0';
const PURPLE_DARK = '#5b4fd1';
const PURPLE_SOFT = 'rgba(124, 111, 240, 0.14)';
const GRADIENT = 'linear-gradient(135deg, #c026d3 0%, #7c6ff0 100%)';
const GRADIENT_HOVER = 'linear-gradient(135deg, #d136e3 0%, #8a7ef5 100%)';

const TEXT = '#f5f5f7';
const TEXT_MUTED = '#9a9aa6';
const BG = '#0a0a0b';
const SURFACE = '#141418';
const SURFACE_RAISED = '#1c1c22';
const SURFACE_HOVER = '#22222a';
const BORDER = '#26262c';
const BORDER_STRONG = '#33333c';

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: PURPLE, light: '#9a8df5', dark: PURPLE_DARK, contrastText: '#fff' },
    background: { default: BG, paper: SURFACE },
    text: { primary: TEXT, secondary: TEXT_MUTED },
    divider: BORDER,
    success: { main: '#22c55e' },
    error: { main: '#f87171' },
    warning: { main: '#fbbf24' },
    info: { main: PURPLE },
    action: {
      hover: SURFACE_HOVER,
      selected: SURFACE_RAISED,
    },
  },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: 14, lineHeight: 1.55 },
    body2: { fontSize: 13, lineHeight: 1.55, color: TEXT_MUTED },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.4), 0 1px 1px rgba(0,0,0,0.3)',
    '0 2px 4px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.35)',
    '0 4px 8px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.4)',
    ...Array(21).fill('0 8px 24px rgba(0,0,0,0.55)'),
  ] as unknown as ReturnType<typeof createTheme>['shadows'],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: BG,
          color: TEXT,
          WebkitFontSmoothing: 'antialiased',
        },
        '::-webkit-scrollbar': { width: 10, height: 10 },
        '::-webkit-scrollbar-track': { background: BG },
        '::-webkit-scrollbar-thumb': { background: BORDER_STRONG, borderRadius: 8 },
        '::-webkit-scrollbar-thumb:hover': { background: '#44444f' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: 16,
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: { backgroundColor: 'transparent', borderBottom: 'none', boxShadow: 'none' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: SURFACE, borderRight: `1px solid ${BORDER}` },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 999, paddingInline: 18, paddingBlock: 8 },
        containedPrimary: {
          backgroundImage: GRADIENT,
          backgroundColor: 'transparent',
          color: '#fff',
          '&:hover': { backgroundImage: GRADIENT_HOVER, backgroundColor: 'transparent' },
          '&.Mui-disabled': {
            backgroundImage: 'none',
            backgroundColor: BORDER,
            color: TEXT_MUTED,
          },
        },
        outlined: {
          borderColor: BORDER_STRONG,
          color: TEXT,
          backgroundColor: SURFACE,
          '&:hover': { backgroundColor: SURFACE_HOVER, borderColor: '#3f3f49' },
        },
        text: {
          color: TEXT,
          '&:hover': { backgroundColor: SURFACE_HOVER },
        },
        sizeLarge: { paddingBlock: 11, fontSize: 14 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 500 },
        filled: { backgroundColor: PURPLE_SOFT, color: '#b9b0ff' },
        colorSuccess: { backgroundColor: 'rgba(34,197,94,0.16)', color: '#86efac' },
        colorError: { backgroundColor: 'rgba(248,113,113,0.16)', color: '#fca5a5' },
        colorInfo: { backgroundColor: PURPLE_SOFT, color: '#b9b0ff' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          marginInline: 8,
          marginBlock: 2,
          paddingBlock: 8,
          color: TEXT_MUTED,
          '& .MuiListItemIcon-root': {
            minWidth: 36,
            color: TEXT_MUTED,
          },
          '&:hover': { backgroundColor: SURFACE_HOVER },
          '&.Mui-selected': {
            backgroundColor: PURPLE_SOFT,
            color: '#b9b0ff',
            fontWeight: 600,
            '& .MuiListItemIcon-root': { color: '#b9b0ff' },
            '& .MuiListItemText-primary': { fontWeight: 600 },
            '&:hover': { backgroundColor: 'rgba(124, 111, 240, 0.2)' },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: 14 },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: SURFACE_RAISED,
          color: TEXT,
          '& fieldset': { borderColor: BORDER },
          '&:hover fieldset': { borderColor: BORDER_STRONG },
          '&.Mui-focused fieldset': { borderColor: PURPLE, borderWidth: 1 },
        },
        input: {
          '&::placeholder': { color: TEXT_MUTED, opacity: 1 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: BORDER, color: TEXT },
        head: {
          color: TEXT_MUTED,
          fontWeight: 500,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          backgroundColor: 'transparent',
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: BORDER } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12 },
        standardError: { backgroundColor: 'rgba(248,113,113,0.12)', color: '#fca5a5' },
        standardSuccess: { backgroundColor: 'rgba(34,197,94,0.12)', color: '#86efac' },
        standardWarning: { backgroundColor: 'rgba(251,191,36,0.12)', color: '#fcd34d' },
        standardInfo: { backgroundColor: PURPLE_SOFT, color: '#b9b0ff' },
      },
    },
    MuiSlider: {
      styleOverrides: {
        rail: { backgroundColor: BORDER, opacity: 1 },
        track: { backgroundColor: PURPLE, borderColor: PURPLE },
        thumb: { backgroundColor: PURPLE },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { backgroundColor: SURFACE_RAISED, border: `1px solid ${BORDER}` },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: SURFACE_RAISED, border: `1px solid ${BORDER}`, color: TEXT },
      },
    },
  },
});

export const tokens = {
  PURPLE,
  PURPLE_SOFT,
  GRADIENT,
  TEXT,
  TEXT_MUTED,
  BG,
  SURFACE,
  SURFACE_RAISED,
  SURFACE_HOVER,
  BORDER,
  BORDER_STRONG,
  sectionGap: 4,
  cardPadding: { xs: 3, md: 4 },
};

export function softShadow() {
  return `0 1px 2px ${alpha('#000', 0.4)}, 0 1px 1px ${alpha('#000', 0.3)}`;
}
